import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Saldos HTC
 * Define las columnas y el formato específico para exportación a PDF y Excel
 */
export default class SaldosHTCExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Reporte de Saldos - HTC');
  }

  getTitle(context) {
    return 'REPORTE DE SALDOS - HTC';
  }

  /**
   * Sobrescribir getMetadata para incluir el periodo del reporte HTC
   * @param {Array} data - Datos del reporte
   * @param {Object} context - Contexto con helpers y configuración
   * @returns {Object} { title, subtitle, operatorias, fechaConsulta, fechaGeneracion, periodo }
   */
  getMetadata(data, context) {
    const metadata = super.getMetadata(data, context);

    // Agregar periodo si existe en el contexto
    if (context.periodoSaldosHtc) {
      metadata.periodo = context.periodoSaldosHtc;
    }

    return metadata;
  }

  /**
   * Configuración preferida del PDF para el reporte HTC
   * Fuerza uso de A3 para asegurar que todas las columnas entren correctamente
   * @param {Number} totalColumns - Cantidad de columnas del reporte
   * @returns {Object} Configuración del PDF
   */
  getPreferredPDFConfig(totalColumns) {
    console.log("PDF Export HTC: " + totalColumns + " columnas detectadas - Forzando A3 landscape");

    return {
      usarPortrait: false,  // Siempre landscape
      usarA3: true,         // Forzar A3 para que entren todas las columnas
      customSize: null,
      advertenciaUsuario: false,
      factorReduccion: 0.90,  // Reducción leve para aprovechar espacio
      fontSize: 6,            // Tamaño de letra cómodo
      cellPadding: 1.5,       // Padding adecuado
      minCellHeight: 6.5,     // Altura mínima de celda
    };
  }


  /**
   * Define las columnas del reporte de Saldos HTC
   * Orden específico según requerimientos
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Código de concepto de ingreso (siempre "Cuota")
    columns.push({
      key: 'codigo_concepto',
      header: 'Código de concepto de ingreso',
      pdfWidth: 30,
      excelWidth: 25,
      align: 'left',

      format: (item, ctx) => 'Cuota',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''
    });

    // 2. Cuenta Patrimonial Asociada (columna vacía)
    columns.push({
      key: 'cuenta_patrimonial',
      header: 'Cuenta Patrimonial Asociada',
      pdfWidth: 30,
      excelWidth: 25,
      align: 'left',

      format: (item, ctx) => '',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'
    });

    // 3. NOMBRE O RAZON SOCIAL
    columns.push({
      key: 'nombre_razon_social',
      header: 'NOMBRE O RAZON SOCIAL',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.nombre_razon_social) return '-';
        return item.nombre_razon_social.toUpperCase();
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 4. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.cuit || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Fecha de otorgamiento
    columns.push({
      key: 'fecha_otorgamiento',
      header: 'Fecha de otorgamiento',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_otorgamiento),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Fecha vencimiento de la Obligación
    columns.push({
      key: 'fecha_vencimiento_obligacion',
      header: 'Fecha vencimiento de la Obligación',
      pdfWidth: 25,
      excelWidth: 22,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_vencimiento_obligacion),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha último Vto de la Obligación
    columns.push({
      key: 'fecha_ultimo_vencimiento',
      header: 'Fecha último Vto de la Obligación',
      pdfWidth: 25,
      excelWidth: 22,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_ultimo_vencimiento),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Saldo Inicial del periodo
    columns.push({
      key: 'saldo_inicial_periodo',
      header: 'Saldo Inicial del periodo',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.saldo_inicial_periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.saldo_inicial_periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const valor = parseFloat(item.saldo_inicial_periodo) || 0;
          return total + valor;
        }, 0);
      }
    });

    // 10. Intereses Compensatorios (1)
    columns.push({
      key: 'intereses_compensatorios',
      header: 'Intereses Compensatorios (1)',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.intereses_compensatorios) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.intereses_compensatorios) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const valor = parseFloat(item.intereses_compensatorios) || 0;
          return total + valor;
        }, 0);
      }
    });

    // 11. Débitos del Periodo (2)
    columns.push({
      key: 'debitos_del_periodo',
      header: 'Débitos del Periodo (2)',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.debitos_del_periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.debitos_del_periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const valor = parseFloat(item.debitos_del_periodo) || 0;
          return total + valor;
        }, 0);
      }
    });

    // 12. Créditos del Periodo (3)
    columns.push({
      key: 'creditos_del_periodo',
      header: 'Créditos del Periodo (3)',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.creditos_del_periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.creditos_del_periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const valor = parseFloat(item.creditos_del_periodo) || 0;
          return total + valor;
        }, 0);
      }
    });

    // 13. Saldo final del Periodo
    columns.push({
      key: 'saldo_final_periodo',
      header: 'Saldo final del Periodo',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.saldo_final_periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.saldo_final_periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const valor = parseFloat(item.saldo_final_periodo) || 0;
          return total + valor;
        }, 0);
      }
    });

    // 14. Estado de Gestión de Cobranzas
    columns.push({
      key: 'estado_morosidad',
      header: 'Estado de Gestión de Cobranzas',
      pdfWidth: 25,
      excelWidth: 22,
      align: 'left',

      format: (item, ctx) => item.estado_morosidad || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 15. Responsable de la gestión (columna vacía)
    columns.push({
      key: 'responsable_gestion',
      header: 'Responsable de la gestión',
      pdfWidth: 25,
      excelWidth: 22,
      align: 'left',

      format: (item, ctx) => '',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 16. ESTADO MANUAL
    columns.push({
      key: 'estado_manual',
      header: 'ESTADO MANUAL',
      pdfWidth: 20,
      excelWidth: 18,
      align: 'left',

      format: (item, ctx) => item.estado_manual || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    return columns;
  }
}
